import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  Input,
  Button,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";

import Loaders from "components/Common/Loaders";
import { fetchMasterPermissionsList } from "model/PermissionModel";
import {
  getUserTypeById,
  createUserType,
  updateUserType,
} from "model/UserTypeModel";
import { urlEndPoint } from "helpers/GeneralHelper";

import "./user-type-add-edit.scss";

/** Group permission rows by `module`; sort module names and descriptions within each group. */
function groupPermissionsByModule(items = []) {
  const bucket = {};
  (Array.isArray(items) ? items : []).forEach((item) => {
    const mod = String(item?.module ?? "").trim() || "Other";
    if (!bucket[mod]) bucket[mod] = [];
    bucket[mod].push(item);
  });
  Object.keys(bucket).forEach((key) => {
    bucket[key].sort((aValue, bValue) =>
      String(aValue?.description ?? "").localeCompare(String(bValue?.description ?? ""))
    );
  });
  const orderedModules = Object.keys(bucket).sort((aValue, bValue) => aValue.localeCompare(bValue));
  return { bucket, orderedModules };
}

/** Group header + items — `defaultChecked` + `key` remount when selection changes (sync with Set). */
function ModulePermissionBlock({
  group,
  permissions,
  selectedPermissionIds,
  moduleAllSelected,
  onToggleModule,
  onTogglePermission,
}) {
  const allInModule = moduleAllSelected(group);

  return (
    <div className="user-type-module">
      <div className="user-type-module__title">
        <Input
          key={`module-${group}-${allInModule}`}
          type="checkbox"
          className="form-check-input"
          defaultChecked={allInModule}
          onChange={(e) => onToggleModule(group, e.target.checked)}
        />
        <span>{group}</span>
      </div>

      <div className="user-type-grid">
        {(permissions || []).map((perm) => {
          const pid = String(perm?.id ?? "").trim();
          const checked = pid ? selectedPermissionIds.has(pid) : false;
          const labelText =
            String(perm?.description ?? perm?.code ?? pid ?? "").trim() || pid;
          return (
            <label
              key={pid}
              className={`user-type-item ${checked ? "is-selected" : ""}`}
              htmlFor={`user-type-perm-${pid}`}
            >
              <Input
                key={`perm-${pid}-${checked}`}
                id={`user-type-perm-${pid}`}
                type="checkbox"
                className="form-check-input"
                defaultChecked={checked}
                aria-label={labelText}
                onChange={(e) => onTogglePermission(pid, e.target.checked)}
              />
              <span className="user-type-item__label">{labelText}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

const UserTypeAddEdit = () => {
  const navigate = useNavigate();
  const { userTypeId } = useParams();
  const isEditMode = Boolean(userTypeId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [permissionRows, setPermissionRows] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(() => new Set());

  const [userType, setUserType] = useState("");
  const [status, setStatus] = useState("active");
  const [multipleTeam, setMultipleTeam] = useState(true);
  const [uniqueClient, setUniqueClient] = useState(false);
  const [teamRequired, setTeamRequired] = useState(false);

  const { bucket: permissionsByModule, orderedModules } = useMemo(
    () => groupPermissionsByModule(permissionRows),
    [permissionRows]
  );

  const allPermissionIds = useMemo(
    () =>
      (Array.isArray(permissionRows) ? permissionRows : [])
        .map((p) => String(p?.id ?? "").trim())
        .filter(Boolean),
    [permissionRows]
  );

  const allPermissionsSelected =
    allPermissionIds.length > 0 &&
    allPermissionIds.every((id) => selectedPermissionIds.has(id));

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const permRes = await fetchMasterPermissionsList();
        const rawData = permRes?.data ?? permRes ?? [];
        const list = Array.isArray(rawData) ? rawData : [];
        if (cancelled) return;
        setPermissionRows(list);

        if (userTypeId) {
          const utRes = await getUserTypeById(userTypeId);
          const data = utRes?.data ?? {};
          if (cancelled) return;

          setUserType(String(data?.name ?? ""));
          const changeLowercase = String(data?.status ?? "active").toLowerCase();
          setStatus(changeLowercase === "deactive" || changeLowercase === "inactive" ? "deactive" : "active");
          setMultipleTeam(Boolean(data?.multipleTeam));
          setUniqueClient(Boolean(data?.uniqueClient));
          setTeamRequired(Boolean(data?.teamRequired));

          const ids = Array.isArray(data?.permissions) ? data.permissions : [];
          setSelectedPermissionIds(new Set(ids.map((x) => String(x))));
        } else {
          setSelectedPermissionIds(
            new Set(
              list.map((p) => String(p?.id ?? "").trim()).filter(Boolean)
            )
          );
          setMultipleTeam(true);
          setUniqueClient(false);
          setTeamRequired(false);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Unable to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [userTypeId]);

  const togglePermissionId = (permId, checked) => {
    const id = String(permId ?? "").trim();
    if (!id) return;
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllPermissions = (checked) => {
    setSelectedPermissionIds(
      checked ? new Set(allPermissionIds) : new Set()
    );
  };

  const toggleModulePermissions = (moduleKey, checked) => {
    const rows = permissionsByModule[moduleKey] || [];
    const ids = rows
      .map((p) => String(p?.id ?? "").trim())
      .filter(Boolean);
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const moduleAllSelected = (moduleKey) => {
    const rows = permissionsByModule[moduleKey] || [];
    const ids = rows
      .map((p) => String(p?.id ?? "").trim())
      .filter(Boolean);
    return ids.length > 0 && ids.every((id) => selectedPermissionIds.has(id));
  };

  const isSuccessResponse = (res) =>
    res?.success !== false && (res?.success || res?.isSuccess || res?.data != null);

  const handleSubmit = async () => {
    const trimmed = String(userType ?? "").trim();
    if (!trimmed) {
      toast.error("User type name is required");
      return;
    }

    /** Checked permissions only — id strings (`PUT user/UserType/{id}`). */
    const permissions = Array.from(selectedPermissionIds).map(String);

    const payload = {
      name: trimmed,
      status,
      multipleTeam,
      uniqueClient,
      teamRequired,
      permissions,
    };

    setSaving(true);
    try {
      const res = isEditMode
        ? await updateUserType(String(userTypeId).trim(), payload)
        : await createUserType(payload);

      if (!isSuccessResponse(res)) {
        toast.error(res?.message || "Unable to save user type");
        return;
      }

      toast.success(res?.message || "Update successfully");
      navigate(`/${urlEndPoint.userType}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content user-type-add-edit-page">
        <Container fluid>
          <Card className="user-type-page-card mb-3">
            <CardBody className="user-type-page-body">
              <h4 className="user-type-page-title">
                {isEditMode ? "Update User Type" : "Create User Type"}
              </h4>
              <hr className="mt-3 mb-3" />

              {loading ? (
                <Loaders />
              ) : (
                <>
                  <div className="user-type-form-section">
                    <Row className="mb-3">
                      <Col md="6">
                        <FormGroup>
                          <Label>
                            User Type <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            value={userType}
                            onChange={(e) => setUserType(e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md="4">
                        <div className="d-flex align-items-center user-type-option-row">
                          <Input
                            key={`multipleTeam-${multipleTeam}`}
                            type="checkbox"
                            className="me-2"
                            defaultChecked={multipleTeam}
                            aria-label="Multiple team"
                            onChange={(e) => setMultipleTeam(e.target.checked)}
                          />
                          <span>Multiple team</span>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="d-flex align-items-center user-type-option-row">
                          <Input
                            key={`uniqueClient-${uniqueClient}`}
                            type="checkbox"
                            className="me-2"
                            defaultChecked={uniqueClient}
                            aria-label="Unique client"
                            onChange={(e) => setUniqueClient(e.target.checked)}
                          />
                          <span>Unique client</span>
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="d-flex align-items-center user-type-option-row">
                          <Input
                            key={`teamRequired-${teamRequired}`}
                            type="checkbox"
                            className="me-2"
                            defaultChecked={teamRequired}
                            aria-label="Team required"
                            onChange={(e) => setTeamRequired(e.target.checked)}
                          />
                          <span>Team required</span>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className="user-type-toolbar">
                    {/* <span className="user-type-summary">
                      <i className="fa fa-check-square-o" aria-hidden="true" />
                      {selectedPermissionIds.size} of {allPermissionIds.length} selected
                    </span> */}
                    <label
                      className="user-type-select-all mt-3"
                      htmlFor="user-type-select-all-cb"
                      role="group"
                      aria-label="Select all permissions"
                    >
                      <Input
                        key={`global-all-${allPermissionsSelected}-${selectedPermissionIds.size}`}
                        id="user-type-select-all-cb"
                        type="checkbox"
                        className="form-check-input"
                        defaultChecked={allPermissionsSelected}
                        disabled={allPermissionIds.length === 0}
                        aria-label="Select all permissions"
                        onChange={(e) => toggleAllPermissions(e.target.checked)}
                      />
                      <span className="user-type-select-all-label">Select all permissions</span>
                    </label>
                  </div>

                  {orderedModules.map((group) => (
                    <ModulePermissionBlock
                      key={group}
                      group={group}
                      permissions={permissionsByModule[group] || []}
                      selectedPermissionIds={selectedPermissionIds}
                      moduleAllSelected={moduleAllSelected}
                      onToggleModule={toggleModulePermissions}
                      onTogglePermission={togglePermissionId}
                    />
                  ))}

                  <div className="user-type-footer">
                    <Button color="primary" onClick={handleSubmit} disabled={saving}>
                      {saving ? "Loading…" : isEditMode ? "Update" : "Create"}
                    </Button>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

UserTypeAddEdit.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(UserTypeAddEdit);
